import * as React from 'react';

/** Matches ProgramCurriculumCollections `referenceCollectionId` union (preset vs org reference). */
export type ReferenceCollectionIdMember =
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_presetCollectionIdMember';
      presetCollectionId?: { id?: string | null } | null;
    }
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_orgCollectionIdMember';
      orgCollectionId?: { id?: string | null } | null;
    };

export type ReferenceCollectionConfig = {
  enableSync?: boolean | null;
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/** Shape needed to read a preset reference id (shared by context + collection fragments). */
export type ReferenceCollectionConfigurationLike = {
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/**
 * Reference library id for synced curriculum tracks: preset library or org collection.
 * Used the same way for tracking (e.g. isPartOfReorderedCollection) regardless of reference type.
 */
export const getSyncedReferenceCollectionId = (
  config: ReferenceCollectionConfigurationLike | undefined
): string | undefined => {
  const ref = config?.referenceCollectionId;
  if (!ref) return undefined;
  if ('presetCollectionId' in ref && ref.presetCollectionId?.id) {
    return ref.presetCollectionId.id;
  }
  if ('orgCollectionId' in ref && ref.orgCollectionId?.id) {
    return ref.orgCollectionId.id;
  }
  return undefined;
};

type ReferenceCollectionConfigContextType = {
  /** Map of collection ID to reference collection configuration from ProgramCurriculumCollections. */
  referenceCollectionConfigById: Record<string, ReferenceCollectionConfig>;
};

const ReferenceCollectionConfigContext = React.createContext<ReferenceCollectionConfigContextType>({
  referenceCollectionConfigById: {},
});

export const useReferenceCollectionConfig = () => React.useContext(ReferenceCollectionConfigContext);

/**
 * Returns true if the collection is synced to a reference library (preset or org collection).
 * Use with collection IDs from PageConfig (Collection_Collection.id) matched to ProgramCurriculumCollections.
 */
export const useIsPartOfReorderedCollection = (collectionId: string | undefined): boolean => {
  const { referenceCollectionConfigById } = useReferenceCollectionConfig();
  if (!collectionId) return false;
  const config = referenceCollectionConfigById[collectionId];
  return config?.enableSync === true && Boolean(getSyncedReferenceCollectionId(config));
};

export default ReferenceCollectionConfigContext;
